import { useEffect, useState } from "react";
import { Button, Layout, Menu, Typography } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import {
  AppstoreOutlined,
  ArrowUpOutlined,
  HomeOutlined,
  LogoutOutlined,
  MobileOutlined,
  QuestionCircleOutlined,
  TagOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { logout } from "../../redux/slices/authSlice";

const { Sider } = Layout;
const { Text } = Typography;

export default function ResellerSidebar() {
  const [balance, setBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const selectedKey = (() => {
    const p = location.pathname;

    if (p === "/") return "home";
    if (p.startsWith("/plans") || p.startsWith("/package")) return "plans";
    if (p.startsWith("/assign-sim-package")) return "assign-sim";
    if (p.startsWith("/topUpEsim")) return "topup";
    if (p.startsWith("/balance")) return "balance";
    if (p.startsWith("/clients")) return "clients";
    if (p.startsWith("/pricing")) return "pricing";
    if (p.startsWith("/help-support")) return "help";

    return "home";
  })();

  useEffect(() => {
    const fetchBalance = async () => {
      setLoadingBalance(true);
      try {
        const res = await api.get("/pos/Resellers/getBalance");
        const value = Number(res.data);
        setBalance(Number.isFinite(value) ? value : null);
      } catch {
        setBalance(null);
      } finally {
        setLoadingBalance(false);
      }
    };

    fetchBalance();
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const menuItems = [
    {
      key: "home",
      icon: <HomeOutlined />,
      label: <Link to="/">Home</Link>,
    },
    ...(user?.isEsimReseller
      ? [
          {
            key: "plans",
            icon: <AppstoreOutlined />,
            label: <Link to="/plans">Plans</Link>,
          },
          {
            key: "topup",
            icon: <ArrowUpOutlined />,
            label: <Link to="/topUpEsim">Top up</Link>,
          },
        ]
      : [
          {
            key: "assign-sim",
            icon: <MobileOutlined />,
            label: <Link to="/assign-sim-package">SIM Packages</Link>,
          },
        ]),
    {
      key: "balance",
      icon: <WalletOutlined />,
      label: <Link to="/balance">Transactions</Link>,
    },
    ...(user?.isEsimReseller
      ? [
          {
            key: "clients",
            icon: <TeamOutlined />,
            label: <Link to="/clients">My Clients</Link>,
          },
        ]
      : []),
  ];

  const secondaryItems = [
    {
      key: "pricing",
      icon: <TagOutlined />,
      label: <Link to="/pricing">Pricing</Link>,
    },
    {
      key: "help",
      icon: <QuestionCircleOutlined />,
      label: <Link to="/help-support">Help & Support</Link>,
    },
  ];

  return (
    <Sider width={240} theme="light" className="side-nav side-nav--reseller">
      <div className="side-nav__content">
        <div className="side-nav__balance">
          <Text type="secondary" className="side-nav__label">
            Balance
          </Text>
          <div className="side-nav__balance-value">
            <WalletOutlined />
            <span>
              {loadingBalance
                ? "Loading..."
                : balance != null
                ? `${balance.toFixed(2)}€`
                : "—"}
            </span>
          </div>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          className="side-nav__menu side-nav__menu--main"
          items={menuItems}
        />

        <div className="side-nav__bottom">
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            className="side-nav__menu side-nav__menu--secondary"
            items={secondaryItems}
          />
          <div className="side-nav__logout">
            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="side-nav__logout-button"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
    </Sider>
  );
}
